import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Globe } from 'lucide-react';
import { Container, Button } from '../components/ui';
import {
  AskIstatecAI,
  ServiceCard,
  ClientLogoCarousel,
  IstaLabsTeaserSection,
} from '../components/sections';
import { WHAT_WE_DO_SERVICES } from '../data/whatWeDo';

/*
 * Home
 * ----
 * Homepage rebuilt from scratch, drawing content from the pages that
 * followed it (About, Services, IstaLabs, Investors) so a first-time
 * visitor sees an accurate cross-section of everything Istatec does
 * before drilling into any single chapter.
 *
 * Section flow — designed to walk the visitor through:
 *
 *   1. Hero          — big video cover, brand tagline, primary CTAs
 *   2. Who we are    — split layout with text (slides in from left)
 *                       and cover video (slides in from right). Links
 *                       to /about for the full story.
 *   3. Stats         — real numbers only: 15+ clients, 2+ countries,
 *                       >98% CSAT, India HQ.
 *   4. Services grid — full-bleed listing of all 11 services
 *                       (reuses the shared ServiceCard component).
 *   5. Ask Istatec AI — existing conversational-AI section, kept.
 *   6. Client logos  — shared right-to-left marquee.
 *   7. IstaLabs      — teaser strip with the infographic sliding in
 *                       from the right, linking to /istalabs.
 *
 * The global <ConnectSection/> (mounted in Layout.tsx) renders
 * automatically below every non-/contact route, so the CTA form
 * appears above the footer without being added here.
 *
 * Every image / video in the body sections has a slide-in-on-scroll
 * animation (text and media enter from opposite edges) per client
 * direction.
 */

// Media used on this page. `HERO_MEDIA` fills the hero background;
// `WHY_CHOOSE_INTRO` is the brand intro clip that plays inside the
// "Why choose Istatec?" section. The IstaLabs teaser strip near the
// bottom of the page is a shared `<IstaLabsTeaserSection />`
// component that owns its own media reference.
const HERO_MEDIA =
  'https://istatec-assets.istatec.com/istatec_cover_final.webm';
const WHY_CHOOSE_INTRO =
  'https://istatec-assets.istatec.com/istatec_logo_intro-part1.mp4';

export const Home: React.FC = () => (
  <>
    <HomeHero />
    {/* AI-chat CTA sits directly beneath the hero so it acts as the
        first substantive action a visitor sees — "you can try our
        AI right here, right now" — before we walk them through
        who / what / how below. */}
    <AskIstatecAI />
    {/* "Why choose Istatec?" — intro-video + pitch paragraph. Sits
        in the "About us" slot the AboutSnapshotSection used to
        occupy, but with the brand intro mp4 on the left and the
        client-friendly pitch copy on the right. */}
    <WhyChooseIstatecSection />
    <HomeStatsSection />
    <HomeServicesSection />
    <ClientLogoCarousel
      heading="Trusted by teams shipping the work"
      subheading="A cross-section of enterprises and fast-growing companies we've delivered for."
    />
    <IstaLabsTeaserSection />
  </>
);

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/**
 * Homepage hero — video-background section whose height is driven by
 * the Container's vertical padding (pt-20 offset for the fixed header,
 * then py-24 → py-32 → py-40 for content breathing room). Deliberately
 * NOT using the `calc(56.25vw - 100px)` responsive-height trick used
 * on the /services / /investors / /istalabs heroes — the homepage
 * hero has its own height rhythm that predates those pages, and
 * clients recognize it.
 */
const HomeHero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <section className="relative overflow-hidden bg-navy-950 pt-20">
      {/* The source has baked-in letterboxing. Crop proportionally so both
          bands stay outside the hero at every viewport size. */}
      <video
        ref={videoRef}
        src={HERO_MEDIA}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute top-1/2 left-0 w-full h-[180%] -translate-y-1/2 object-cover"
        aria-hidden
      />
      {/* Vertical dark overlay so the copy reads clearly regardless of
          which video frame is on screen at any moment. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-black from-[80px] via-navy-950/50 to-navy-950"
      />

      <Container className="relative z-10 py-24 md:py-32 lg:py-40">
        <div className="max-w-4xl mx-auto text-center">
          {/* Small brand line above the headline */}
          <motion.p
            className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-white/85 mb-5 md:mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            Istatec — Technology Consulting
          </motion.p>

          {/* Headline — framed around AI-led transformation, with the
              second half in accent-blue so the "AI" beat lands
              visually. Matches the old homepage's positioning while
              being sharper and less marketing-fluffy. */}
          <motion.h1
            className="text-[32px] sm:text-5xl md:text-6xl lg:text-[64px] font-bold text-white leading-[1.12] mb-6 md:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            Reimagining businesses with{' '}
            <span className="text-accent-300"><span className="whitespace-nowrap">AI-led</span> transformation</span>
            <span className="text-white">.</span>
          </motion.h1>

          <motion.p
            className="text-lg md:text-xl lg:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto mb-9 md:mb-11"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            We help enterprises stay ahead of what&rsquo;s coming next —
            combining senior technical advisors, best-sourced expert
            engineers, and an AI-first approach to build faster than
            competitors can.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <Link to="/services">
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Explore our services
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                variant="outline"
                size="lg"
                className="bg-white/10 hover:bg-white/20 border-white/30 hover:border-white/60 text-white"
              >
                Talk to our team
              </Button>
            </Link>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

/**
 * "Why choose Istatec?" — split layout, FIRST of two on the homepage.
 *
 * Zig-zag rule: this section puts TEXT on the LEFT and the brand
 * intro VIDEO on the RIGHT. The next split section on the page
 * (IstaLabs teaser, further down) flips it — image LEFT, text RIGHT.
 * Alternating media/text positions as the visitor scrolls creates a
 * "Z pattern" that keeps the page from feeling repetitive.
 *
 * The intro video autoplays muted and loops. Content is Istatec-
 * specific with no dummy numbers: we cite only the honest facts
 * already established elsewhere on the site (mid-scale team, 15+
 * clients, 2+ countries, >98% CSAT, India-based).
 *
 * `overflow-hidden` on the section clips the slide-in animations'
 * off-screen start positions so they don't cause a horizontal
 * scrollbar mid-transition.
 */
const WhyChooseIstatecSection: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <section className="bg-white py-16 md:py-24 lg:py-28 overflow-hidden">
      <div className="site-wide-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left column — pitch copy, slides in from the left.
              DOM-first so on mobile the headline lands before the
              video block. */}
          <motion.div
            className="max-w-2xl"
            initial={{ opacity: 0, x: -100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-accent-600 mb-5">
              Why choose Istatec?
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-4xl lg:text-[52px] font-bold text-navy-950 leading-[1.16] mb-6 md:mb-8">
              People at the heart of every experience.
            </h2>
            <div className="space-y-5 text-lg md:text-xl lg:text-xl font-normal text-navy-900 leading-relaxed">
              <p>
                Istatec is a mid-scale technology consultancy specializing
                in AI-led digital transformation, digital engineering, and
                experience design. Our people-first approach pairs deep
                engineering with human-centered thinking to deliver
                measurable business outcomes.
              </p>
              <p>
                Headquartered in India, we&rsquo;ve delivered for 15+
                enterprises across BFSI, healthcare, consumer, and
                enterprise SaaS — with a{' '}
                <span className="font-bold">
                  &gt;98% customer satisfaction score
                </span>{' '}
                and a deliberately mid-scale team so every engagement gets
                senior attention, not one of 10,000 line-items on a
                global P&amp;L.
              </p>
            </div>
            <div className="mt-8">
              <Link to="/about">
                <Button
                  variant="primary"
                  size="lg"
                  rightIcon={<ArrowRight className="w-5 h-5" />}
                >
                  Learn more about us
                </Button>
              </Link>
            </div>
          </motion.div>

          {/* Right column — brand intro video, slides in from the
              right. Autoplays muted + looped. On mobile this stacks
              beneath the text column since the DOM order is text
              first, video second. */}
          <motion.div
            className="relative w-full aspect-video rounded-2xl overflow-hidden bg-navy-900 shadow-[0_25px_60px_-15px_rgba(2,8,20,0.35)] ring-1 ring-navy-100"
            initial={{ opacity: 0, x: 200 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <video
              ref={videoRef}
              src={WHY_CHOOSE_INTRO}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="absolute inset-0 w-full h-full object-cover"
              aria-hidden
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * "Our work in numbers" — same 4-stat row treatment used on the About
 * page's Stats section (real numbers only). Editorial `border-top-2`
 * per stat, big bold navy number, small muted label.
 */
const HomeStatsSection: React.FC = () => (
  <section className="bg-navy-50/60 py-14 md:py-20">
    <Container>
      <motion.p
        className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-accent-600 mb-3"
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        Our work in numbers
      </motion.p>
      <motion.h2
        className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-950 leading-[1.16] mb-10 md:mb-14 max-w-3xl"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.45, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
      >
        Honest track record, real outcomes.
      </motion.h2>
      <motion.div
        className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      >
        <Stat number="15+" label="Happy clients we've served" />
        <Stat number="2+" label="Countries served" />
        <Stat number=">98%" label="Customer satisfaction score" />
        <Stat icon={Globe} label="Ready to ship globally" />
      </motion.div>
    </Container>
  </section>
);

/**
 * Single stat tile — either a big text "number" (e.g. "15+", ">98%")
 * or an icon in its place. Uses a discriminated pair of optional
 * props so callers can pass whichever fits: text numbers for
 * quantitative stats, an icon for qualitative "we're globally
 * ready" style claims.
 */
const Stat: React.FC<{
  number?: string;
  icon?: React.ElementType;
  label: string;
}> = ({ number, icon: Icon, label }) => (
  <div className="border-t-2 border-navy-900 pt-5">
    {Icon ? (
      <Icon
        className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 text-navy-900 mb-3"
        strokeWidth={1.75}
        aria-hidden
      />
    ) : (
      <p className="text-4xl md:text-5xl lg:text-[56px] font-bold text-navy-900 mb-3 leading-none">
        {number}
      </p>
    )}
    <p className="text-sm md:text-[15px] text-navy-600 leading-relaxed">
      {label}
    </p>
  </div>
);

/**
 * "What we do" — full-bleed services grid mirroring /services. All 11
 * services from `WHAT_WE_DO_SERVICES` are rendered using the shared
 * `ServiceCard` so the grid stays visually identical to every other
 * services grid on the site.
 */
const HomeServicesSection: React.FC = () => (
  <section className="bg-white py-16 md:py-20 lg:py-24">
    <div className="site-wide-container">
      <motion.p
        className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-accent-600 mb-3"
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        What we do
      </motion.p>
      <motion.h2
        className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-950 leading-[1.16] mb-10 md:mb-14 max-w-3xl"
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
      >
        Full-stack services designed to help businesses grow, adapt, and lead.
      </motion.h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 md:gap-x-8 lg:gap-x-10 gap-y-14 md:gap-y-16">
        {WHAT_WE_DO_SERVICES.map((s, i) => (
          <ServiceCard
            key={s.id}
            service={s}
            animationDelay={Math.min(i, 5) * 0.06}
          />
        ))}
      </div>

      {/* Overflow CTA — one more chance for visitors to jump into the
          full services listing without scrolling further. */}
      <div className="mt-12 md:mt-14">
        <Link to="/services">
          <Button
            variant="outline"
            size="lg"
            rightIcon={<ArrowRight className="w-5 h-5" />}
          >
            View all services
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// `<IstaLabsTeaserSection />` — imported from `../components/sections`
// so both the homepage and the About page render the exact same
// teaser strip. The local definition that used to live here has been
// retired.
